import React, { useEffect, useMemo, useState } from 'react';
import {
	Box,
	Flex,
	Heading,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	HStack,
	Button,
	Input,
	FormControl,
	FormLabel,
	Textarea,
	Spinner,
	Text,
	Badge,
	Stack,
	useToast,
	NumberInput,
	NumberInputField,
	IconButton,
	SimpleGrid,
	Center,
	InputGroup,
	InputRightElement,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalCloseButton,
	ModalBody,
	ModalFooter,
} from '@chakra-ui/react';
import { ArrowBackIcon, CloseIcon, SearchIcon } from '@chakra-ui/icons';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import AdminNavbar from '../../component/adminNavbar';
import Alert from '../../component/alert';
import { updatePriceGroup } from '../../redux/actions/priceGroupActions';
import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';
const authConfig = () => ({ headers: { 'x-auth-token': localStorage.getItem('x-auth-token') } });

type GroupItem = {
	key: string;
	product_id: number;
	variant_id?: number | null;
	variant_label?: string;
	product_name: string;
	price: string;
	fb_number?: string;
};

const itemKey = (product_id: number, variant_id?: number | null) =>
	variant_id ? `v-${variant_id}` : `p-${product_id}`;

const EditPriceGroup = ({ updatePriceGroup }) => {
	const { id } = useParams();
	const navigate = useNavigate();
	const toast = useToast();

	const [loading, setLoading] = useState(true);
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [items, setItems] = useState<GroupItem[]>([]);
	const [originalName, setOriginalName] = useState('');
	const [originalDescription, setOriginalDescription] = useState('');
	const [originalItems, setOriginalItems] = useState<GroupItem[]>([]);
	const [searchResults, setSearchResults] = useState<any[]>([]);
	const [productsLoading, setProductsLoading] = useState(false);
	const [productSearch, setProductSearch] = useState('');
	const [hasSearched, setHasSearched] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [priceFilter, setPriceFilter] = useState('');
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);

	useEffect(() => {
		const load = async () => {
			setLoading(true);
			try {
				const groupRes = await axios.get(`${BASE_URL}/api/price-groups/${id}`, authConfig());
				const group = groupRes.data;
				const loadedItems = (group.items || []).map((i: any) => ({
					key: itemKey(i.product_id, i.variant_id),
					product_id: i.product_id,
					variant_id: i.variant_id || null,
					variant_label: i.variant_label || '',
					product_name: i.product_name,
					price: String(i.price),
					fb_number: i.fb_number || '',
				}));
				setName(group.name || '');
				setDescription(group.description || '');
				setItems(loadedItems);
				setOriginalName(group.name || '');
				setOriginalDescription(group.description || '');
				setOriginalItems(loadedItems);
			} catch {
				toast({ title: 'Failed to load price group.', status: 'error', duration: 3000 });
				navigate('/admin/price-groups');
			} finally {
				setLoading(false);
			}
		};
		load();
	}, [id]);

	const handleSearchProducts = async () => {
		const q = productSearch.trim();
		if (!q) {
			setSearchResults([]);
			setHasSearched(false);
			toast({ title: 'Enter a product name or FB number to search.', status: 'info', duration: 2000 });
			return;
		}
		setProductsLoading(true);
		setHasSearched(true);
		try {
			const res = await axios.get(`${BASE_URL}/api/products/list`, {
				...authConfig(),
				params: { q },
			});
			setSearchResults(res.data || []);
		} catch {
			setSearchResults([]);
			toast({ title: 'Product search failed.', status: 'error', duration: 2000 });
		} finally {
			setProductsLoading(false);
		}
	};

	const handleSelectProduct = (product: any) => {
		if (!product) return;
		const productId = product.product_id;
		const variants = product.variants || [];

		if (Number(product.has_variants) === 1 && variants.length > 0) {
			const newRows = variants
				.filter((v: any) => !items.find((i) => i.variant_id === v.variant_id))
				.map((v: any) => ({
					key: itemKey(productId, v.variant_id),
					product_id: productId,
					variant_id: v.variant_id,
					variant_label: v.label || `Variant #${v.variant_id}`,
					product_name: product.name || '',
					price: String(parseFloat(v.price) || 0),
					fb_number: product.fb_number || '',
				}));
			if (newRows.length === 0) {
				toast({ title: 'All variants already in group.', status: 'warning', duration: 2000 });
				return;
			}
			setItems((prev) => [...prev, ...newRows]);
			toast({ title: `Added ${newRows.length} variant(s) of "${product.name}".`, status: 'success', duration: 2000 });
			return;
		}

		if (items.find((i) => i.product_id === productId && !i.variant_id)) {
			toast({ title: 'Product already in group.', status: 'warning', duration: 2000 });
			return;
		}
		setItems((prev) => [
			...prev,
			{
				key: itemKey(productId),
				product_id: productId,
				variant_id: null,
				product_name: product.name || '',
				price: String(parseFloat(product.price) || 0),
				fb_number: product.fb_number || '',
			},
		]);
		toast({ title: `"${product.name}" added.`, status: 'success', duration: 2000 });
	};

	const handleRemoveItem = (key: string) => {
		setItems((prev) => prev.filter((i) => i.key !== key));
	};

	const handleUpdateItemPrice = (key: string, price: string) => {
		setItems((prev) => prev.map((i) => (i.key === key ? { ...i, price } : i)));
	};

	const labelFor = (item: GroupItem) =>
		item.variant_label ? `${item.product_name} — ${item.variant_label}` : item.product_name;

	const changes = useMemo(() => {
		const originalMap = new Map(originalItems.map((i) => [i.key, i]));
		const currentMap = new Map(items.map((i) => [i.key, i]));

		const added = items.filter((i) => !originalMap.has(i.key));
		const removed = originalItems.filter((i) => !currentMap.has(i.key));
		const priceChanged = items
			.filter((i) => originalMap.has(i.key))
			.map((i) => {
				const prev = originalMap.get(i.key)!;
				const oldPrice = parseFloat(prev.price) || 0;
				const newPrice = parseFloat(i.price) || 0;
				if (oldPrice === newPrice) return null;
				return { ...i, oldPrice, newPrice };
			})
			.filter(Boolean) as (GroupItem & { oldPrice: number; newPrice: number })[];

		const metaChanged = {
			name: name.trim() !== originalName.trim(),
			description: description !== originalDescription,
		};

		const hasChanges =
			added.length > 0 ||
			removed.length > 0 ||
			priceChanged.length > 0 ||
			metaChanged.name ||
			metaChanged.description;

		return { added, removed, priceChanged, metaChanged, hasChanges };
	}, [items, originalItems, name, originalName, description, originalDescription]);

	const openPreview = () => {
		if (!name.trim()) {
			toast({ title: 'Name is required.', status: 'warning', duration: 2000 });
			return;
		}
		if (!changes.hasChanges) {
			toast({ title: 'No changes to save.', status: 'info', duration: 2000 });
			return;
		}
		setIsPreviewOpen(true);
	};

	const handleConfirmSave = async () => {
		const payload = items.map((i) => ({
			product_id: i.product_id,
			variant_id: i.variant_id || null,
			price: parseFloat(i.price) || 0,
		}));
		setIsSaving(true);
		try {
			await updatePriceGroup(parseInt(id as string), name.trim(), description, payload);
			setIsPreviewOpen(false);
			navigate('/admin/price-groups');
		} finally {
			setIsSaving(false);
		}
	};

	const isProductFullyAdded = (p: any) => {
		const variants = p.variants || [];
		if (Number(p.has_variants) === 1 && variants.length > 0) {
			return variants.every((v: any) => items.find((i) => i.variant_id === v.variant_id));
		}
		return !!items.find((i) => i.product_id === p.product_id && !i.variant_id);
	};

	const availableSearchResults = searchResults.filter((p) => !isProductFullyAdded(p));

	const visibleItems = useMemo(() => {
		const q = priceFilter.trim().toLowerCase();
		if (!q) return items;
		return items.filter((i) => {
			return (
				(i.product_name || '').toLowerCase().includes(q) ||
				(i.variant_label || '').toLowerCase().includes(q) ||
				(i.fb_number || '').toLowerCase().includes(q)
			);
		});
	}, [items, priceFilter]);

	const groupedItems = useMemo(() => {
		const map = new Map<number, { product_name: string; fb_number?: string; rows: GroupItem[] }>();
		for (const item of visibleItems) {
			if (!map.has(item.product_id)) {
				map.set(item.product_id, {
					product_name: item.product_name,
					fb_number: item.fb_number,
					rows: [],
				});
			}
			map.get(item.product_id)!.rows.push(item);
		}
		return Array.from(map.entries());
	}, [visibleItems]);

	if (loading) {
		return (
			<>
				<AdminNavbar />
				<Box ml={{ base: 0, md: '250px' }} p={10} display="flex" justifyContent="center">
					<Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
				</Box>
			</>
		);
	}

	return (
		<>
			<Alert />
			<AdminNavbar />
			<Box pt={{ base: 16, md: 6 }} px={{ base: 4, md: 6 }} pb={24} ml={{ base: 0, md: '250px' }} bg="gray.50" minH="100vh">
				<Flex align="center" justify="space-between" mb={6} gap={3} flexWrap="wrap">
					<HStack spacing={3}>
						<IconButton
							aria-label="Back"
							icon={<ArrowBackIcon />}
							variant="ghost"
							onClick={() => navigate('/admin/price-groups')}
						/>
						<Box>
							<Heading fontSize="2xl" fontWeight="bold" color="gray.700">
								{name || 'Edit Price Group'}
							</Heading>
							<Text fontSize="sm" color="gray.500" mt={0.5}>
								{items.length} price{items.length === 1 ? '' : 's'} in this group
							</Text>
						</Box>
					</HStack>
					<HStack>
						<Button variant="ghost" onClick={() => navigate('/admin/price-groups')}>
							Cancel
						</Button>
						<Button colorScheme="blue" onClick={openPreview}>
							Preview & Save
						</Button>
					</HStack>
				</Flex>

				<Stack spacing={6}>
					<Box bg="white" p={5} borderRadius="lg" boxShadow="sm">
						<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
							<FormControl isRequired>
								<FormLabel>Name</FormLabel>
								<Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Tier A" />
							</FormControl>
							<FormControl>
								<FormLabel>Description</FormLabel>
								<Textarea
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									rows={1}
									placeholder="Optional notes"
								/>
							</FormControl>
						</SimpleGrid>
					</Box>

					{/* Prices — primary focus */}
					<Box bg="white" p={5} borderRadius="lg" boxShadow="sm">
						<Flex justify="space-between" align="center" mb={4} gap={3} flexWrap="wrap">
							<Box>
								<Text fontWeight="bold" fontSize="lg" color="gray.700">
									Prices in this group
								</Text>
								<Text fontSize="sm" color="gray.500">
									Showing {visibleItems.length} of {items.length}
								</Text>
							</Box>
							<Input
								placeholder="Filter prices…"
								size="sm"
								maxW="260px"
								value={priceFilter}
								onChange={(e) => setPriceFilter(e.target.value)}
							/>
						</Flex>

						{items.length === 0 ? (
							<Center py={12} border="1px dashed" borderColor="gray.200" borderRadius="md" bg="gray.50">
								<Stack spacing={1} textAlign="center">
									<Text fontSize="md" color="gray.500" fontWeight="medium">
										No prices yet
									</Text>
									<Text fontSize="sm" color="gray.400">
										Search for a product below to add it.
									</Text>
								</Stack>
							</Center>
						) : visibleItems.length === 0 ? (
							<Center py={8}>
								<Text fontSize="sm" color="gray.400">No prices match this filter.</Text>
							</Center>
						) : (
							<Box overflowX="auto" border="1px solid" borderColor="gray.200" borderRadius="md">
								<Table size="md" variant="simple">
									<Thead bg="gray.100">
										<Tr>
											<Th>Product / Option</Th>
											<Th>FB #</Th>
											<Th isNumeric w="140px">Group Price</Th>
											<Th w="50px"></Th>
										</Tr>
									</Thead>
									<Tbody>
										{groupedItems.map(([productId, group]) => (
											<React.Fragment key={productId}>
												{group.rows.length > 1 && (
													<Tr bg="gray.50">
														<Td colSpan={4} py={2}>
															<HStack spacing={2}>
																<Text fontSize="sm" fontWeight="semibold" color="gray.700">
																	{group.product_name}
																</Text>
																{group.fb_number ? (
																	<Badge colorScheme="purple" fontSize="xs">{group.fb_number}</Badge>
																) : null}
																<Badge colorScheme="teal" fontSize="xs">{group.rows.length} options</Badge>
															</HStack>
														</Td>
													</Tr>
												)}
												{group.rows.map((item) => (
													<Tr key={item.key} _hover={{ bg: 'blue.50' }}>
														<Td py={3}>
															{group.rows.length > 1 ? (
																<Text fontSize="sm" pl={3} color="gray.700">
																	{item.variant_label || 'Option'}
																</Text>
															) : (
																<HStack spacing={2} flexWrap="wrap">
																	<Text fontSize="sm" fontWeight="medium">{item.product_name}</Text>
																	{item.variant_label ? (
																		<Badge colorScheme="teal" fontSize="xs">{item.variant_label}</Badge>
																	) : null}
																</HStack>
															)}
														</Td>
														<Td>
															{item.fb_number ? (
																<Badge colorScheme="purple" fontSize="xs">{item.fb_number}</Badge>
															) : (
																<Text color="gray.300" fontSize="sm">—</Text>
															)}
														</Td>
														<Td isNumeric>
															<NumberInput
																size="md"
																value={item.price}
																onChange={(v) => handleUpdateItemPrice(item.key, v)}
																min={0}
																w="120px"
																ml="auto"
															>
																<NumberInputField
																	fontWeight="semibold"
																	textAlign="right"
																	onWheel={(e) => e.currentTarget.blur()}
																/>
															</NumberInput>
														</Td>
														<Td>
															<IconButton
																aria-label="Remove"
																icon={<CloseIcon />}
																size="sm"
																colorScheme="red"
																variant="ghost"
																onClick={() => handleRemoveItem(item.key)}
															/>
														</Td>
													</Tr>
												))}
											</React.Fragment>
										))}
									</Tbody>
								</Table>
							</Box>
						)}
					</Box>

					{/* Search / add — secondary */}
					<Box bg="white" p={5} borderRadius="lg" boxShadow="sm">
						<Text fontWeight="semibold" mb={1} color="gray.700">
							Add Products
						</Text>
						<Text fontSize="sm" color="gray.500" mb={3}>
							Search by name or FB number, then click a result to add it.
						</Text>
						<HStack mb={3} spacing={2} align="stretch">
							<InputGroup size="md" maxW="420px">
								<Input
									placeholder="Product name or FB number…"
									value={productSearch}
									onChange={(e) => setProductSearch(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											e.preventDefault();
											handleSearchProducts();
										}
									}}
									autoComplete="off"
								/>
								<InputRightElement width="3rem">
									<IconButton
										aria-label="Search"
										icon={<SearchIcon />}
										size="sm"
										variant="ghost"
										onClick={handleSearchProducts}
										isLoading={productsLoading}
									/>
								</InputRightElement>
							</InputGroup>
							<Button colorScheme="teal" onClick={handleSearchProducts} isLoading={productsLoading}>
								Search
							</Button>
						</HStack>

						{!hasSearched ? (
							<Center py={6} border="1px dashed" borderColor="gray.200" borderRadius="md" bg="gray.50">
								<Text fontSize="sm" color="gray.400">
									Nothing loaded yet — search to find products.
								</Text>
							</Center>
						) : productsLoading ? (
							<Center py={8}><Spinner size="sm" /></Center>
						) : availableSearchResults.length === 0 ? (
							<Center py={6}>
								<Text fontSize="sm" color="gray.400">
									{searchResults.length > 0
										? 'All matching products are already in this group.'
										: 'No products found.'}
								</Text>
							</Center>
						) : (
							<Box maxH="260px" overflowY="auto" border="1px solid" borderColor="gray.200" borderRadius="md">
								{availableSearchResults.map((p) => (
									<Flex
										key={p.product_id}
										as="button"
										type="button"
										w="100%"
										textAlign="left"
										px={4}
										py={3}
										align="center"
										justify="space-between"
										gap={2}
										borderBottom="1px solid"
										borderColor="gray.100"
										_hover={{ bg: 'teal.50' }}
										onClick={() => handleSelectProduct(p)}
									>
										<Box minW={0}>
											<HStack spacing={2} align="center">
												<Text fontSize="sm" fontWeight="medium" noOfLines={1}>{p.name}</Text>
												{Number(p.has_variants) === 1 ? (
													<Badge colorScheme="teal" fontSize="xs">
														{(p.variants || []).length || ''} options
													</Badge>
												) : null}
												{p.fb_number ? (
													<Badge colorScheme="purple" fontSize="xs">{p.fb_number}</Badge>
												) : null}
											</HStack>
										</Box>
										<Text fontSize="xs" color="teal.600" fontWeight="semibold" flexShrink={0}>
											{Number(p.has_variants) === 1 ? 'Add variants' : 'Add'}
										</Text>
									</Flex>
								))}
							</Box>
						)}
					</Box>
				</Stack>

				<Box
					display={{ base: 'block', md: 'none' }}
					position="fixed"
					bottom={0}
					left={0}
					right={0}
					bg="white"
					borderTop="1px solid"
					borderColor="gray.200"
					p={3}
					zIndex={10}
				>
					<Button w="full" colorScheme="blue" onClick={openPreview}>
						Preview & Save
					</Button>
				</Box>

				{/* Preview Changes Modal */}
				<Modal isOpen={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} size="lg" isCentered scrollBehavior="inside">
					<ModalOverlay />
					<ModalContent maxH="85vh">
						<ModalHeader>Preview Changes</ModalHeader>
						<ModalCloseButton />
						<ModalBody>
							<Stack spacing={4}>
								{(changes.metaChanged.name || changes.metaChanged.description) && (
									<Box>
										<Text fontWeight="semibold" mb={2} fontSize="sm" color="gray.600">Details</Text>
										<Stack spacing={1} fontSize="sm">
											{changes.metaChanged.name && (
												<Text>
													Name: <Text as="span" color="gray.400" textDecoration="line-through">{originalName || '—'}</Text>
													{' → '}
													<Text as="span" fontWeight="semibold">{name.trim()}</Text>
												</Text>
											)}
											{changes.metaChanged.description && (
												<Text>
													Description updated
												</Text>
											)}
										</Stack>
									</Box>
								)}

								{changes.added.length > 0 && (
									<Box>
										<HStack mb={2}>
											<Badge colorScheme="green">Added</Badge>
											<Text fontSize="sm" color="gray.500">{changes.added.length}</Text>
										</HStack>
										<Box border="1px solid" borderColor="green.100" borderRadius="md" overflow="hidden">
											{changes.added.map((item) => (
												<Flex key={item.key} px={3} py={2} justify="space-between" borderBottom="1px solid" borderColor="green.50" bg="green.50">
													<Text fontSize="sm">{labelFor(item)}</Text>
													<Text fontSize="sm" fontWeight="semibold">${(parseFloat(item.price) || 0).toFixed(2)}</Text>
												</Flex>
											))}
										</Box>
									</Box>
								)}

								{changes.removed.length > 0 && (
									<Box>
										<HStack mb={2}>
											<Badge colorScheme="red">Removed</Badge>
											<Text fontSize="sm" color="gray.500">{changes.removed.length}</Text>
										</HStack>
										<Box border="1px solid" borderColor="red.100" borderRadius="md" overflow="hidden">
											{changes.removed.map((item) => (
												<Flex key={item.key} px={3} py={2} justify="space-between" borderBottom="1px solid" borderColor="red.50" bg="red.50">
													<Text fontSize="sm">{labelFor(item)}</Text>
													<Text fontSize="sm" color="gray.500">${(parseFloat(item.price) || 0).toFixed(2)}</Text>
												</Flex>
											))}
										</Box>
									</Box>
								)}

								{changes.priceChanged.length > 0 && (
									<Box>
										<HStack mb={2}>
											<Badge colorScheme="orange">Price changed</Badge>
											<Text fontSize="sm" color="gray.500">{changes.priceChanged.length}</Text>
										</HStack>
										<Box border="1px solid" borderColor="orange.100" borderRadius="md" overflow="hidden">
											{changes.priceChanged.map((item) => (
												<Flex key={item.key} px={3} py={2} justify="space-between" align="center" gap={3} borderBottom="1px solid" borderColor="orange.50" bg="orange.50">
													<Text fontSize="sm" flex={1}>{labelFor(item)}</Text>
													<HStack spacing={1} fontSize="sm">
														<Text color="gray.400" textDecoration="line-through">${item.oldPrice.toFixed(2)}</Text>
														<Text>→</Text>
														<Text fontWeight="semibold">${item.newPrice.toFixed(2)}</Text>
													</HStack>
												</Flex>
											))}
										</Box>
									</Box>
								)}

								{!changes.hasChanges && (
									<Text fontSize="sm" color="gray.400">No changes detected.</Text>
								)}
							</Stack>
						</ModalBody>
						<ModalFooter borderTop="1px solid" borderColor="gray.100">
							<Button variant="ghost" mr={3} onClick={() => setIsPreviewOpen(false)}>
								Back to Edit
							</Button>
							<Button colorScheme="blue" onClick={handleConfirmSave} isLoading={isSaving}>
								Confirm Save
							</Button>
						</ModalFooter>
					</ModalContent>
				</Modal>
			</Box>
		</>
	);
};

const mapDispatchToProps = { updatePriceGroup };

export default connect(null, mapDispatchToProps)(EditPriceGroup);
